import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './common/Modal';
import { Button, ButtonVariant } from './common/Button';
import { formatDate } from '../utils/formatters';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  options: Array<{
    label: string;
    action: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
  }>;
  onClose: () => void;
  /**
   * Während laufender Verarbeitung (z. B. Rechnungs-Submit, #340) true:
   * alle Optionen sind deaktiviert und Klicks werden ignoriert.
   */
  isProcessing?: boolean;
  dataComparison?: {
    original: Record<string, any>;
    modified: Record<string, any>;
    displayFields?: Array<{ key: string, label: string }>;
  };
}

/**
 * Confirmation/decision dialog used across the invoice/offer/customer flows.
 *
 * M-UX-2.4: internal structure migrated onto `common/Modal`
 * (architecture-ux-restructure.md §4.3) — the central portal/focus-trap/
 * Escape/scroll-lock/focus-return primitive replaces the hand-rolled
 * equivalents this component used to carry itself. The PUBLIC
 * `ConfirmationDialogProps` facade (isOpen/title/message/options/onClose/
 * dataComparison) is byte-identical to the pre-migration version — no
 * consumer needs to change (Consumer-Blast-Radius = 0, all 14 usages across
 * InvoiceDashboard/OfferDashboard/XRechnungDashboard/CustomerManagementPage/
 * MobileInvoiceDashboard/etc. keep working unmodified).
 *
 * Behaviour parity: Escape still closes (now via Modal's own listener),
 * a click on the dimmed backdrop still closes (now Modal's overlay div),
 * clicking an option still invokes its `action` (wrapped in the same
 * try/catch → console.error as before) and the dialog. `role="dialog"` now
 * lives on the actual panel (Modal's contract) rather than the outer
 * full-screen wrapper — this is the correct a11y placement and an
 * intentional structural change; it does not affect any consumer prop.
 */
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  options,
  onClose,
  isProcessing = false,
  dataComparison
}) => {
  const { t, i18n } = useTranslation('common');

  // Eigener Re-Entry-Schutz (#340): ab dem ersten Klick gesperrt, bis die
  // (ggf. asynchrone) Aktion fertig ist — auch bei Fehler wird freigegeben.
  // Ref blockiert im selben Tick, State sorgt für die sichtbare Deaktivierung.
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const busy = isProcessing || pending;

  const handleOptionClick = (action: () => void | Promise<unknown>) => {
    if (isProcessing || pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    const release = () => {
      pendingRef.current = false;
      setPending(false);
    };
    try {
      const result = action() as unknown;
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        (result as Promise<unknown>)
          .catch((error) => console.error('Fehler beim Ausführen der Dialog-Aktion:', error))
          .finally(release);
        return;
      }
      release();
    } catch (error) {
      console.error('Fehler beim Ausführen der Dialog-Aktion:', error);
      release();
    }
  };

  const renderComparisonData = () => {
    if (!dataComparison) return null;

    const { original, modified, displayFields = [] } = dataComparison;

    const getDisplayValue = (obj: Record<string, any> | null, key: string) => {
      if (!obj) return t('confirmationDialog.notAvailable');
      const value = obj[key];

      if (value === undefined || value === null) return '-';
      if (typeof value === 'object') {
        if (value instanceof Date) return formatDate(value, { locale: i18n.language });
        return JSON.stringify(value);
      }
      return String(value);
    };

    const changedFields = displayFields.filter(({ key }) => {
      const originalValue = getDisplayValue(original, key);
      const modifiedValue = getDisplayValue(modified, key);
      return originalValue !== modifiedValue;
    });

    if (changedFields.length === 0) {
      return (
        <div className="mt-4 text-sm text-muted">
          <p>{t('confirmationDialog.noChanges')}</p>
        </div>
      );
    }

    return (
      <div className="mt-4 text-sm">
        <h3 className="font-medium text-primary">
          {t('confirmationDialog.changesCount', { count: changedFields.length })}
        </h3>
        <div className="mt-2 border border-default rounded-control overflow-hidden">
          <table className="min-w-full divide-y divide-default">
            <thead className="bg-surface-inset">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">{t('confirmationDialog.fieldLabel')}</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">{t('confirmationDialog.originalLabel')}</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">{t('confirmationDialog.newLabel')}</th>
              </tr>
            </thead>
            <tbody className="bg-surface-raised divide-y divide-default">
              {changedFields.map(({ key, label }) => {
                const originalValue = getDisplayValue(original, key);
                const modifiedValue = getDisplayValue(modified, key);

                return (
                  <tr key={key}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-primary">{label}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-muted max-w-xs truncate">{originalValue}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-primary max-w-xs truncate font-medium">{modifiedValue}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Modal itself already renders nothing while `open` is false, but keep this
  // explicit early return so `renderComparisonData`/footer construction never
  // runs for a closed dialog either (parity with the pre-migration guard).
  if (!isOpen) return null;

  // Visual order preserved from the pre-migration markup: callers consistently
  // pass `options` as [primary/confirm, secondary/cancel, ...] and relied on
  // `sm:flex-row-reverse` to put the primary action on the right. `common/Modal`'s
  // footer is a plain `justify-end` row, so the render order is reversed here
  // to keep the primary action rightmost without requiring every call site to
  // reorder its `options` array.
  const footer = (
    <>
      {[...options].reverse().map((option, index) => (
        <Button
          key={index}
          type="button"
          variant={(option.variant ?? 'primary') as ButtonVariant}
          disabled={busy}
          onClick={() => handleOptionClick(option.action)}
        >
          {option.label}
        </Button>
      ))}
    </>
  );

  return (
    <Modal open={isOpen} onClose={onClose} title={title} footer={footer}>
      <p className="text-sm text-secondary">{message}</p>
      {renderComparisonData()}
    </Modal>
  );
};
